import {
  ChangeLogs,
  Checkbox,
  DateInput,
  formatBalance,
  formatDate,
  IconLoader,
  PermissionDenied,
  PermissionEnum,
  ToggleInput,
} from '@ui/src';
import { CustomerStatus } from '../../../hoc/common/types';
import { AccountsSettingsPanelTypes } from './types';
import AccountBalance from '../balance/balance';

import cn from './styles.module.css';

const AccountSettingsPanel = ({
  selectedTab,
  setSelectedTab,
  account,
  creditBlock,
  setCreditBlock,
  setBlockTopupPopupVisible,
  organizationStatus,
  debitBlock,
  setDebitBlock,
  setBlockWithdrawPopupVisible,
  visibleToClient,
  setVisibleToClient,
  closingBalancesAt,
  setClosingBalancesAt,
  getCustomerAccountClosingBalancesAction,
  clearClosingBalances,
  closedBalances,
  overdraftEnabled,
  accountOverdraftEnabled,
  onUpdateOverdraftStatus,
  overdraftUpdateInProgress,
  balancesIsLoading,
  balancesIsFetching,
  baseCurrencyBalance,
  currencies,
  currenciesList,
  accountsBalances,
  setBalances,
  updateOverdraftBalanceIsLoading,
  defaultBalances,
  balanceWithCurrencyName,
  closed,
  setClosePopupVisible,
  activePermissions,
  isLoading,
  accountsLogs,
  users,
  accountsLogsPagination,
  isFetching,
  getChangeLogsAction,
  selectedAccount,
  ffSettings,
}: AccountsSettingsPanelTypes) => (
  <div className={cn.accountSettingsContent}>
    <div className={cn.accountTabs}>
      <button
        type="button"
        className={`${cn.accountTab} ${
          selectedTab === 'settings' ? cn.active : ''
        }`}
        onClick={() => setSelectedTab('settings')}
      >
        Settings
      </button>
      <button
        type="button"
        className={`${cn.accountTab} ${
          selectedTab === 'logs' ? cn.active : ''
        }`}
        onClick={() => setSelectedTab('logs')}
      >
        Logs
      </button>
    </div>
    {selectedTab === 'settings' && (
      <>
        <div className={cn.accountSettingsTitle}>Account blocking</div>
        <div className={cn.blockAccountWrapper}>
          <Checkbox
            type="checkbox"
            name={`creditBlock-${account?.id}`}
            checked={creditBlock}
            onChange={(e) => {
              setCreditBlock(e.target.checked);

              setBlockTopupPopupVisible(true);
            }}
            title="Block top-up"
            disabled={organizationStatus === CustomerStatus.CLOSED}
          />
        </div>
        <div className={cn.blockAccountWrapper}>
          <Checkbox
            type="checkbox"
            name={`debitBlock-${account?.id}`}
            checked={debitBlock}
            onChange={(e) => {
              setDebitBlock(e.target.checked);

              setBlockWithdrawPopupVisible(true);
            }}
            title="Block withdraw"
            disabled={organizationStatus === CustomerStatus.CLOSED}
          />
        </div>
        <div className={cn.accountSettingsTitle}>Account visibility</div>
        <div className={cn.blockAccountWrapper}>
          <Checkbox
            type="radio"
            name={`visibleToClient-${account?.id}`}
            checked={visibleToClient}
            onChange={() => setVisibleToClient(true)}
            title="Visible to the client"
            disabled={organizationStatus === CustomerStatus.CLOSED}
          />
        </div>
        <div className={cn.blockAccountWrapper}>
          <Checkbox
            type="radio"
            name={`notVisibleToClient-${account?.id}`}
            checked={!visibleToClient}
            onChange={() => setVisibleToClient(false)}
            title="Not visible to the client"
            disabled={organizationStatus === CustomerStatus.CLOSED}
          />
        </div>

        <div className={cn.balances}>
          <div className={cn.closingBalanceWrapper}>
            <DateInput
              placeholder="Select balance date"
              value={closingBalancesAt}
              onChange={(e) => {
                setClosingBalancesAt(new Date(e).toISOString());

                getCustomerAccountClosingBalancesAction({
                  accountId: account?.id as string,
                  date: new Date(e).toISOString(),
                });
              }}
              onReset={() => {
                setClosingBalancesAt('');
                clearClosingBalances();
              }}
              withoutCustomSelector
            />
          </div>

          <div
            className={`${cn.balancesTop} ${
              closedBalances ? cn.flexStart : ''
            }`}
          >
            <div className={cn.balancesTitle}>Balance</div>
            {closedBalances && (
              <div
                className={cn.balancesTitle}
              >{`Balance on ${formatDate(closingBalancesAt)}`}</div>
            )}
            {overdraftEnabled && (
              <div className={cn.overdraftToggleWrapper}>
                <ToggleInput
                  name={`accountOverdraftEnabled-${account?.id}`}
                  checked={accountOverdraftEnabled}
                  title="Overdraft"
                  onChange={(e) =>
                    onUpdateOverdraftStatus({
                      enabled: e.target.checked,
                      accountId: account?.id as string,
                    })
                  }
                  disabled={
                    overdraftUpdateInProgress ||
                    organizationStatus === CustomerStatus.CLOSED
                  }
                />
              </div>
            )}
          </div>
          <div className={cn.balancesWrapper}>
            {(balancesIsLoading || balancesIsFetching) && (
              <div className={cn.closingBalancesLoader}>
                <IconLoader />
              </div>
            )}
            {baseCurrencyBalance && (
              <AccountBalance
                balance={{
                  ...baseCurrencyBalance,
                  currencyName: currencies?.find(
                    (currency) =>
                      currency.value === baseCurrencyBalance.currencyId,
                  )?.suffix,
                }}
                currenciesList={currenciesList}
                overdraftEnabled={overdraftEnabled && accountOverdraftEnabled}
                balances={accountsBalances}
                setBalances={setBalances}
                updateOverdraftBalanceIsLoading={
                  updateOverdraftBalanceIsLoading
                }
                defaultBalance={defaultBalances?.find(
                  (balanceValue) => balanceValue.id === baseCurrencyBalance.id,
                )}
                closedBalance={
                  closedBalances
                    ?.find(
                      (item) =>
                        item.currencyId === baseCurrencyBalance.currencyId,
                    )
                    ?.value?.toString()
                    ? formatBalance(
                        closedBalances?.find(
                          (item) =>
                            item.currencyId === baseCurrencyBalance.currencyId,
                        )?.value as number,
                        currenciesList?.find(
                          (item) => item.id === baseCurrencyBalance.currencyId,
                        ),
                      )
                    : undefined
                }
              />
            )}
            {balanceWithCurrencyName
              ?.filter(
                (item) => item.currencyId !== baseCurrencyBalance?.currencyId,
              )
              .sort((a, b) => a?.currencyName?.localeCompare(b?.currencyName))
              .map((item) => (
                <AccountBalance
                  key={item.id}
                  balance={item}
                  currenciesList={currenciesList}
                  overdraftEnabled={overdraftEnabled && accountOverdraftEnabled}
                  balances={accountsBalances}
                  setBalances={setBalances}
                  updateOverdraftBalanceIsLoading={
                    updateOverdraftBalanceIsLoading
                  }
                  defaultBalance={defaultBalances?.find(
                    (balanceValue) => balanceValue.id === item.id,
                  )}
                  closedBalance={
                    closedBalances
                      ?.find(
                        (balanceValue) =>
                          balanceValue.currencyId === item.currencyId,
                      )
                      ?.value?.toString()
                      ? formatBalance(
                          closedBalances?.find(
                            (balanceValue) =>
                              balanceValue.currencyId === item.currencyId,
                          )?.value as number,
                          currenciesList?.find(
                            (currency) => currency.id === item.currencyId,
                          ),
                        )
                      : undefined
                  }
                />
              ))}
          </div>
        </div>
        {organizationStatus !== CustomerStatus.CLOSED &&
          (!closed || (closed && !ffSettings?.ffAccountReopenDisabled)) && (
            <div className={cn.closeAccountWrapper}>
              <button
                type="button"
                className={`${cn.closeAccountButton} ${
                  closed ? cn.openAccountButton : ''
                }`}
                onClick={() => setClosePopupVisible(true)}
              >
                {`${closed ? 'Open' : 'Close'} account`}
              </button>
            </div>
          )}
      </>
    )}
    {selectedTab === 'logs' && (
      <>
        {activePermissions?.includes(PermissionEnum.AUDIT_LOG_LIST) && (
          <ChangeLogs
            entityType="ACCOUNT"
            entityId={selectedAccount as string}
            changeLogsIsLoading={isLoading}
            changeLogs={accountsLogs}
            activePermission={activePermissions}
            users={users}
            pagination={accountsLogsPagination}
            changeLogsIsFetching={isFetching}
            getChangeLogs={getChangeLogsAction}
          />
        )}
        {!activePermissions?.includes(PermissionEnum.AUDIT_LOG_LIST) && (
          <PermissionDenied />
        )}
      </>
    )}
  </div>
);

export default AccountSettingsPanel;
